import React from 'react';
import Spinner from './Spinner';

/**
 * Standardized Loading Components
 * Use these throughout the application for consistent loading states
 */

// Full page loading overlay
interface PageLoaderProps {
  message?: string;
}

export const PageLoader: React.FC<PageLoaderProps> = ({ message = 'Loading...' }) => {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50"
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="text-center">
        <Spinner size="lg" color="blue" className="mx-auto mb-4" aria-hidden="true" />
        <p className="text-gray-600 text-sm">{message}</p>
      </div>
    </div>
  );
};

// Inline loading (for sections)
interface InlineLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  className?: string;
}

export const InlineLoader: React.FC<InlineLoaderProps> = ({
  size = 'md',
  message = 'Loading...',
  className = '',
}) => {
  return (
    <div
      className={`flex items-center justify-center py-8 ${className}`}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="text-center">
        <Spinner size={size} color="blue" className="mx-auto mb-2" aria-hidden="true" />
        {message && <p className="text-gray-600 text-sm">{message}</p>}
      </div>
    </div>
  );
};

// Button loading state
interface ButtonLoaderProps {
  size?: 'sm' | 'md' | 'lg';
}

export const ButtonLoader: React.FC<ButtonLoaderProps> = ({ size = 'sm' }) => {
  return <Spinner size={size} color="white" className="inline-block" />;
};

// Table skeleton loader
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 6 }) => {
  return (
    <div className="overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-6 py-3 text-left">
                <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                  <div
                    className="h-4 bg-gray-200 rounded animate-pulse"
                    style={{ width: `${Math.random() * 40 + 60}%` }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Card skeleton loader
interface CardSkeletonProps {
  count?: number;
}

export const CardSkeleton: React.FC<CardSkeletonProps> = ({ count = 3 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-4 animate-pulse" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-4/6 animate-pulse" />
          </div>
          <div className="mt-4 h-10 bg-gray-200 rounded w-24 animate-pulse" />
        </div>
      ))}
    </div>
  );
};

// List skeleton loader
interface ListSkeletonProps {
  items?: number;
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({ items = 5 }) => {
  return (
    <div className="space-y-4">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
              <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Form skeleton loader
export const FormSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i}>
          <div className="h-4 bg-gray-200 rounded w-24 mb-2 animate-pulse" />
          <div className="h-10 bg-gray-200 rounded w-full animate-pulse" />
        </div>
      ))}
      <div className="flex gap-4">
        <div className="h-10 bg-gray-200 rounded w-24 animate-pulse" />
        <div className="h-10 bg-gray-200 rounded w-24 animate-pulse" />
      </div>
    </div>
  );
};

// Stats card skeleton
interface StatsSkeletonProps {
  count?: number;
}

export const StatsSkeleton: React.FC<StatsSkeletonProps> = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded w-20 animate-pulse" />
            <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-8 bg-gray-200 rounded w-32 mb-2 animate-pulse" />
          <div className="h-3 bg-gray-200 rounded w-24 animate-pulse" />
        </div>
      ))}
    </div>
  );
};

// Chart skeleton loader
export const ChartSkeleton: React.FC = () => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
      <div className="h-64 bg-gray-100 rounded flex items-end justify-around p-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="bg-gray-300 rounded-t animate-pulse"
            style={{
              width: '12%',
              height: `${Math.random() * 60 + 40}%`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Loading button wrapper (HOC for buttons)
interface LoadingButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  children: React.ReactNode;
}

export const LoadingButton: React.FC<LoadingButtonProps> = ({
  loading = false,
  disabled,
  children,
  className = '',
  'aria-label': ariaLabel,
  ...props
}) => {
  // Generate accessible label if not provided
  const accessibleLabel = ariaLabel || (loading ? 'Loading, please wait' : undefined);

  return (
    <button
      {...props}
      disabled={disabled || loading}
      aria-label={accessibleLabel}
      aria-busy={loading}
      className={`${className} ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
    >
      {loading ? (
        <span className="flex items-center justify-center">
          <ButtonLoader size="sm" aria-hidden="true" />
          <span className="ml-2">{children}</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
};
